// library imports
import { useState, useEffect, useContext, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  Flex,
  Card,
  Popover,
  Avatar,
  Menu,
  Button,
  Row,
  Col,
  theme,
  Select,
  Typography,
  Grid,
} from "antd";
import { UnorderedListOutlined } from "@ant-design/icons";
// context imports
import { IndexContext } from "../context/IndexContext";
// hook imports
import useNavMenu from "./NavMenu";
// asset imports
import siteLogo from "../assets/site_logo.png";
// component imports
import ProfileMenu from "./ProfileMenu";
import useFetch from "../hooks/useFetch";

function MainLayout() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const screenSize = Grid.useBreakpoint();

  // handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // create a ref to point to the layout DOM
  const layoutRef = useRef();

  // use context
  const index_context = useContext(IndexContext);

  const [fetchWrapper] = useFetch();

  // get the token of current theme
  const { token } = theme.useToken();

  // for navigation
  const navigate = useNavigate();
  const location = useLocation();
  // for top menu collapse to sidebar
  const [collapsed, setCollapsed] = useState(true);
  // for getting current selected menu key
  const [menuSelectedKey, setMenuSelectedKey] = useState(["Dashboard"]);
  // for getting authentication state
  const [authenticationState, setAuthenticationState] = useState({});

  const [loading, setLoading] = useState(false);
  const [siteList, setSiteList] = useState([]);
  const [siteSelected, setSiteSelected] = useState(null);

  // get nav menu
  const [NavMenu, header_height] = useNavMenu(siteSelected);

  // styles
  const headerStyle = {
    textAlign: "center",
    height: header_height,
    paddingInline: 0,
    lineHeight: header_height,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const contentStyle = {
    height: "calc(100% -" + header_height + " )",
    width: "100vw",
    minWidth: "100vw",
    overflow: "auto",
    padding: 10,
  };

  const layoutStyle = {
    overflow: "hidden",
    padding: 0,
    margin: 0,
    width: "100%",
    height: "100vh",
  };

  // height of first row of header
  const flexStyle = {
    height:
      menuSelectedKey !== "Configuration" && windowWidth < 768 ? "50%" : "100%",
  };

  function getAuthenticationState() {
    fetchWrapper({
      endpoint_url: "user_management/checkUser",

      onSuccess: (response) => {
        setAuthenticationState({ ...authenticationState, ...response });
      },

      onResponseError: () => {
        navigate("/login", { replace: true });
        setAuthenticationState({});
      },

      onFetchError: () => {
        navigate("/login", { replace: true });
        setAuthenticationState({});
      },
    });
  }

  /* API to get site selection */
  function getSiteSelection() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "main_layout/getSiteSelection",
      onSuccess: (fetch_output) => {
        setLoading(false);
        if (fetch_output.status === true) {
          setSiteList(fetch_output.site_list);
          if (fetch_output.site_list.length > 0) {
            setSiteSelected(fetch_output.site_list[0].value);
          }
        }
      },
    });
  }

  useEffect(() => {
    getAuthenticationState();
    getSiteSelection();
  }, []);

  useEffect(() => {
    const path = location.pathname.split("?")[0].split("/");
    //setMenuSelectedKey(path.charAt(0).toUpperCase() + path.slice(1));
    setMenuSelectedKey(
      path.map((value) => value.charAt(0).toUpperCase() + value.slice(1))
    );
  }, [location]);

  return (
    <Layout style={layoutStyle} ref={layoutRef}>
      <Layout.Header style={headerStyle}>
        <Card
          style={{
            height: "100%",
            width: "100%",
            borderRadius: 0,
            margin: 0,
          }}
          styles={{
            body: {
              height: "100%",
              width: "100%",
              borderRadius: 0,
              paddingBlock: 0,
              paddingLeft: 12,
            },
          }}
          bordered={false}
        >
          <Row style={{ height: "100%" }}>
            <Col xl={0} flex="52px">
              <Flex align="center" style={{ height: "100%", width: "100%" }}>
                <Button
                  icon={<UnorderedListOutlined />}
                  type="text"
                  size="large"
                  onClick={() => setCollapsed(!collapsed)}
                />
              </Flex>
            </Col>
            <Col flex="auto" style={{ height: "100%", paddingLeft: 0 }}>
              <Flex
                justify="space-between"
                align="center"
                style={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <img
                  src={siteLogo}
                  alt="Xynersys"
                  style={{
                    height: "70%",
                    width: "244px",
                  }}
                />
                <Row style={{ height: "100%", width: "100%" }}>
                  <Col
                    style={{ height: "100%" }}
                    xs={0}
                    sm={0}
                    md={0}
                    lg={0}
                    xl={24}
                  >
                    <Menu
                      style={{
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        borderBottom: 0,
                      }}
                      mode="horizontal"
                      items={
                        authenticationState.user_type === 1 ||
                        authenticationState.user_type === 2
                          ? NavMenu
                          : NavMenu.filter((row) => row.key !== "Configuration")
                      }
                      onClick={(item) => {
                        // check login user before navigate
                        getAuthenticationState();

                        let path = "";
                        item.keyPath.reverse().map((value) => {
                          path = path + "/" + value;
                        });
                        navigate(path);
                      }}
                      onSelect={(item) => {
                        setMenuSelectedKey(item.key);
                      }}
                      selectedKeys={menuSelectedKey}
                    ></Menu>
                  </Col>
                </Row>
                <div>
                  <Flex gap={12} align="center" justify="end">
                    <Row>
                      <Col
                        style={{ height: "100%", width: 200 }}
                        xs={0}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        {!menuSelectedKey.includes("Configuration") && (
                          <Select
                            style={{ width: 200 }}
                            loading={loading}
                            value={siteSelected}
                            options={siteList}
                            labelRender={({ label, value }) => (
                              <div
                                style={{
                                  textAlign: "end",
                                  marginRight: 12,
                                  color: "var(--ant-color-primary)",
                                  fontWeight: "var(--ant-font-weight-strong)",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {label}
                              </div>
                            )}
                            variant="borderless"
                            size="large"
                            onChange={(value, option) => {
                              setSiteSelected(value);
                            }}
                            placeholder="Select site"
                          />
                        )}
                      </Col>
                    </Row>
                    <Popover
                      placement="bottom"
                      contentStyle={{ marginTop: "50px" }}
                      content={<ProfileMenu />}
                      arrow={false}
                      trigger="hover"
                      overlayInnerStyle={{
                        borderRadius: 0,
                        marginTop: 6,
                        padding: 0,
                      }}
                    >
                      <Avatar
                        style={{ backgroundColor: token["blue-6"] }}
                        size={"large"}
                      >
                        DM
                      </Avatar>
                    </Popover>
                  </Flex>
                </div>
              </Flex>
            </Col>
          </Row>
        </Card>
      </Layout.Header>
      <Layout>
        <Layout.Sider
          breakpoint="xl"
          collapsedWidth="0"
          width="300"
          collapsible
          trigger={null}
          collapsed={collapsed}
          onBreakpoint={() => setCollapsed(true)}
        >
          <Menu
            mode="inline"
            inlineIndent={32}
            style={{ height: "100%" }}
            items={NavMenu}
            onClick={(item) => {
              // check login user before navigate
              getAuthenticationState();

              let path = "";
              item.keyPath.reverse().map((value) => {
                path = path + "/" + value;
              });
              navigate(path);
            }}
            onSelect={(item) => setMenuSelectedKey(item.key)}
            selectedKeys={menuSelectedKey}
          ></Menu>
        </Layout.Sider>
        <Layout.Content
          style={contentStyle}
          onFocus={() => {
            setCollapsed(true);
          }}
        >
          {/* add layoutRef into the IndexContext */}

          <IndexContext.Provider
            value={{
              ...index_context,
              layoutRef: layoutRef,
              authenticationState: authenticationState,
              siteSelected: siteSelected,
              siteList: siteList,
            }}
          >
            {screenSize.xs && !menuSelectedKey.includes("Configuration") && (
              <Flex
                style={{
                  position: "sticky",
                  top: -10,
                  background: "var(--ant-color-bg-base)",
                  padding: "4px 10px 4px 10px",
                  margin: "-10px -10px 10px -10px",
                  zIndex: 99,
                }}
                justify="end"
              >
                <Select
                  style={{ width: "100%" }}
                  loading={loading}
                  value={siteSelected}
                  options={siteList}
                  labelRender={({ label, value }) => (
                    <div
                      style={{
                        textAlign: "end",
                        marginRight: 12,
                        color: "var(--ant-color-primary)",
                        fontWeight: "var(--ant-font-weight-strong)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </div>
                  )}
                  variant="borderless"
                  size="large"
                  onChange={(value, option) => {
                    setSiteSelected(value);
                  }}
                  placeholder="Select site"
                />
              </Flex>
            )}
            <Outlet></Outlet>
          </IndexContext.Provider>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export default MainLayout;
